import React, { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/Base/Dialog";
import { Button } from "@/components/Base/Button";
import { useI18n } from "@/i18n/hooks";
import { useCurrencySymbol } from "@/hooks/useUtils";
import utils from "@/utils/utils";
import { navigateByMessagePath } from "@/utils/messagePathNavigation";
import { useNavigation } from "@/utils/navigation";
import { NoticeContentImage } from "./NoticeContentImage";
import type { NoticeItemRaw } from "../types";
import {
  formatNoticeDescription,
  getNoticeTitle,
} from "../utils/formatNoticeContent";
import "./UserNoticeDialog.scss";

export interface UserNoticeDialogProps {
  visible: boolean;
  notice: NoticeItemRaw;
  onClose: () => void | Promise<void>;
  /** 弹窗展示后上报 markPopupShown + markRead */
  onEnsurePopupDisplayed: () => Promise<void>;
  onTrackClick: () => Promise<void>;
}

export const UserNoticeDialog: React.FC<UserNoticeDialogProps> = ({
  visible,
  notice,
  onClose,
  onEnsurePopupDisplayed,
  onTrackClick,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const currencySymbol = useCurrencySymbol();
  const [actionLoading, setActionLoading] = useState(false);

  const noticeTitle = getNoticeTitle(notice, t);
  const description = formatNoticeDescription(notice, currencySymbol);
  const iconUrl = notice.icon ? utils.getImageUrl(notice.icon) : "";
  const messagePath = notice.messagePath?.trim();
  const pathButtonText = notice.pathButtonText?.trim();

  useEffect(() => {
    if (!visible) return;
    void onEnsurePopupDisplayed().catch(error => {
      console.error("mark notice popup displayed failed:", error);
    });
  }, [visible, notice.id, onEnsurePopupDisplayed]);

  const dismiss = useCallback(async () => {
    await onEnsurePopupDisplayed();
    await Promise.resolve(onClose());
  }, [onClose, onEnsurePopupDisplayed]);

  const handleClose = () => {
    if (actionLoading) return;
    void (async () => {
      setActionLoading(true);
      try {
        await dismiss();
      } catch (error) {
        console.error("close user notice dialog failed:", error);
        await Promise.resolve(onClose());
      } finally {
        setActionLoading(false);
      }
    })();
  };

  const handleJump = () => {
    if (actionLoading) return;
    void (async () => {
      setActionLoading(true);
      try {
        await onEnsurePopupDisplayed();
        await onTrackClick();
        await Promise.resolve(onClose());
        if (messagePath) {
          navigateByMessagePath(messagePath, navigate);
        }
      } catch (error) {
        console.error("user notice jump failed:", error);
      } finally {
        setActionLoading(false);
      }
    })();
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      showCloseButton
      closeButtonPosition="bottom-center"
      size="sm"
      className="user-notice-dialog"
      getContainer={() => document.body}
    >
      <div className="user-notice-dialog__body">
        {iconUrl ? (
          <div className="user-notice-dialog__icon-wrap">
            <img
              className="user-notice-dialog__icon"
              src={iconUrl}
              alt={noticeTitle}
            />
          </div>
        ) : null}
        <h3 className="user-notice-dialog__notice-title">{noticeTitle}</h3>
        <div className="user-notice-dialog__content">{description}</div>
        <NoticeContentImage
          extra={notice.extra}
          navigate={navigate}
          onTrackClick={onTrackClick}
          onBeforeInternalNav={onClose}
          alt={noticeTitle}
        />
        {messagePath ? (
          <Button
            variant="primary"
            size="lg"
            block
            className="user-notice-dialog__action"
            disabled={actionLoading}
            onClick={handleJump}
          >
            {pathButtonText || t("message.goNow")}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
};
